import { useSyncExternalStore } from "react";

/*
 * The service worker (built from src/pwa/service-worker.js by scripts/vite-pwa.mjs into /sw.js) and the install prompt.
 * Registered in a production build; in `npm run dev` only with VITE_PWA_DEV=1 (otherwise any worker left from an earlier
 * flagged run is removed, so the dev server is never answered from a cache).
 * A new build waits until the person taps "Reload" in the banner, so a screen already open never mixes two builds.
 */

type BeforeInstall = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> };

type PwaState = {
  /** The browser offered to install (Chrome, Edge, Samsung Internet). */
  canInstall: boolean;
  /** Running as the installed app. */
  installed: boolean;
  /** A new build is downloaded and waiting. */
  updateReady: boolean;
  /** A service worker controls this page. */
  active: boolean;
};

let state: PwaState = { canInstall: false, installed: false, updateReady: false, active: false };
let deferred: BeforeInstall | null = null;
let waiting: ServiceWorker | null = null;
const listeners = new Set<() => void>();
const set = (s: Partial<PwaState>) => {
  state = { ...state, ...s };
  listeners.forEach((l) => l());
};

export const swEnabled = import.meta.env.PROD || import.meta.env.VITE_PWA_DEV === "1";

const standalone = () => window.matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;

/** Called once from main.tsx, before React renders, so the early beforeinstallprompt is not missed. */
export function startPwa() {
  set({ installed: standalone() });
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e as BeforeInstall;
    set({ canInstall: true });
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    set({ canInstall: false, installed: true });
  });
  if (!("serviceWorker" in navigator)) return;
  if (!swEnabled) {
    void navigator.serviceWorker.getRegistrations().then((rs) => rs.forEach((r) => void r.unregister()));
    return;
  }
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js", { scope: "/" }).then(
      (reg) => {
        set({ active: Boolean(navigator.serviceWorker.controller) });
        const watch = (w: ServiceWorker | null) => {
          if (!w) return;
          const ready = () => {
            // The first install has no page to update; later ones wait for the person.
            if (w.state === "installed" && navigator.serviceWorker.controller) {
              waiting = w;
              set({ updateReady: true });
            }
          };
          ready();
          w.addEventListener("statechange", ready);
        };
        watch(reg.waiting);
        reg.addEventListener("updatefound", () => watch(reg.installing));
        // A phone app can stay open for days: look for a new build every hour.
        window.setInterval(() => void reg.update().catch(() => undefined), 3_600_000);
      },
      () => undefined,
    );
    let reloading = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      set({ active: true });
      if (waiting && !reloading) {
        reloading = true;
        window.location.reload();
      }
    });
  });
}

/** Shows the browser's install dialog; false when the browser did not offer one (iPhone: Share, Add to Home Screen). */
export async function install() {
  if (!deferred) return false;
  const d = deferred;
  deferred = null;
  await d.prompt();
  const { outcome } = await d.userChoice;
  set({ canInstall: false });
  return outcome === "accepted";
}

/** Switches to the waiting build; the page reloads when it takes over. */
export function applyUpdate() {
  waiting?.postMessage({ type: "SKIP_WAITING" });
}

export function usePwa() {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => void listeners.delete(l);
    },
    () => state,
  );
}
